// src/hooks/useWebSocket.ts - Real Socket.io client integration
import { useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { Message, TypingIndicator } from '../types/message';
import { getWebSocketUrl } from '../utils/apiHelpers';

export interface WebSocketMessage {
  type: 'message' | 'typing' | 'read_receipt' | 'user_online' | 'user_offline' | 'conversation_update' | 'conversation:join' | 'conversation:leave';
  data: any;
}

export interface RealTimeSimulation {
  isEnabled: boolean;
  messageDelay: number; // milliseconds
  typingDelay: number;
  onlineUsers: string[];
  typingUsers: Map<string, NodeJS.Timeout>;
}

export const useWebSocket = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<ReturnType<typeof io> | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  
  // Real-time simulation state (kept for typed API, but disabled with real socket)
  const simulationRef = useRef<RealTimeSimulation>({
    isEnabled: false,
    messageDelay: 1500,
    typingDelay: 2000,
    onlineUsers: [],
    typingUsers: new Map()
  });

  // Message handlers
  const messageHandlers = useRef<Map<string, (data: any) => void>>(new Map());

  // Register message handler
  const onMessage = useCallback((type: string, handler: (data: any) => void) => {
    messageHandlers.current.set(type, handler);
  }, []);

  // Unregister message handler
  const offMessage = useCallback((type: string) => {
    messageHandlers.current.delete(type);
  }, []);

  // Simulate periodic typing indicators (disabled when real socket is connected)
  const simulatePeriodicTyping = useCallback(() => {
    if (!simulationRef.current.isEnabled) return;

    const typingUsers = ['2', '3', '4'];
    const randomUser = typingUsers[Math.floor(Math.random() * typingUsers.length)];
    
    // Start typing
    const typingIndicator: TypingIndicator = {
      conversationId: `conv-${randomUser}`,
      userId: randomUser,
      isTyping: true,
      timestamp: new Date().toISOString()
    };

    const handler = messageHandlers.current.get('typing');
    if (handler) {
      handler(typingIndicator);
    }

    // Stop typing after delay
    const timeout = setTimeout(() => {
      const stopTyping: TypingIndicator = {
        ...typingIndicator,
        isTyping: false,
        timestamp: new Date().toISOString()
      };
      
      if (handler) {
        handler(stopTyping);
      }
    }, simulationRef.current.typingDelay);

    simulationRef.current.typingUsers.set(randomUser, timeout);

    // Schedule next typing simulation
    setTimeout(simulatePeriodicTyping, 10000 + Math.random() * 20000); // 10-30 seconds
  }, []);

  // Simulate periodic incoming messages (disabled when real socket is connected)
  const simulatePeriodicMessages = useCallback(() => {
    if (!simulationRef.current.isEnabled) return;

    const mockMessages = [
      "Hey! How's your apartment search going?",
      "I found a great place in Brooklyn, want to check it out together?",
      "Are you free this weekend to meet up?",
      "What's your budget range?",
      "I'm looking for someone who's clean and quiet",
      "Do you have any pets?",
      "I work from home, so I need a quiet space",
      "What's your move-in date?",
      "I'm looking for a 2-bedroom, are you interested?",
      "The rent is $1200/month, does that work for you?"
    ];

    const randomMessage = mockMessages[Math.floor(Math.random() * mockMessages.length)];
    const randomUserId = ['2', '3', '4'][Math.floor(Math.random() * 3)];
    const conversationId = `conv-${randomUserId}`;

    const message: Message = {
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      conversationId,
      senderId: randomUserId,
      content: randomMessage,
      timestamp: new Date().toISOString(),
      status: {
        sent: true,
        delivered: true,
        read: false
      },
      type: 'text'
    };

    // Simulate message delay
    setTimeout(() => {
      const handler = messageHandlers.current.get('message');
      if (handler) {
        handler(message);
      }
    }, simulationRef.current.messageDelay);

    // Schedule next message
    setTimeout(simulatePeriodicMessages, 15000 + Math.random() * 30000); // 15-45 seconds
  }, []);

  // Start mock simulation (only if explicitly enabled)
  // NOTE: Currently unused - kept for future testing/debugging
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const startRealTimeSimulation = useCallback(() => {
    if (!simulationRef.current.isEnabled) return;

    // Simulate users coming online
    const onlineUsers = ['2', '3', '4', '5']; // Mock user IDs
    simulationRef.current.onlineUsers = onlineUsers;
    
    // Notify about online users
    onlineUsers.forEach(userId => {
      setTimeout(() => {
        const handler = messageHandlers.current.get('user_online');
        if (handler) {
          handler({ userId, timestamp: new Date().toISOString() });
        }
      }, Math.random() * 3000); // Random delay 0-3 seconds
    });

    // Simulate periodic typing indicators
    simulatePeriodicTyping();
    
    // Simulate occasional messages
    simulatePeriodicMessages();
  }, [simulatePeriodicMessages, simulatePeriodicTyping]);

  // Real Socket.io connect
  const connect = useCallback(() => {
    if (socketRef.current && socketRef.current.connected) {
      return;
    }

    setConnectionStatus('connecting');

    try {
      const url = getWebSocketUrl();
      const token = localStorage.getItem('token') || '';
      // Initialize socket.io client with auth
      const socket = io(`${url}/messaging`, {
        transports: ['websocket'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: maxReconnectAttempts,
        auth: { token },
        path: '/ws',
        timeout: 10000
      });

      socketRef.current = socket;

      socket.on('connect', () => {
        setIsConnected(true);
        setConnectionStatus('connected');
        setError(null);
        reconnectAttempts.current = 0;
      });

      socket.on('disconnect', (reason: string) => {
        setIsConnected(false);
        setConnectionStatus('disconnected');
        
        // Provide user-friendly disconnect messages
        if (reason === 'io server disconnect') {
          // Server disconnected the client (e.g., authentication failed)
          setError('Connection closed by server. Please refresh the page.');
        } else if (reason === 'io client disconnect') {
          // Client disconnected intentionally
          setError(null);
        } else if (reason === 'ping timeout' || reason === 'transport close') {
          // Network issues
          setError('Connection lost. Attempting to reconnect...');
        } else {
          setError('Disconnected from server. Attempting to reconnect...');
        }
      });

      socket.on('connect_error', (err: any) => {
        let errorMessage = 'WebSocket connection error';
        
        // Provide user-friendly error messages
        if (err?.message) {
          if (err.message.includes('authentication') || err.message.includes('401')) {
            errorMessage = 'Authentication failed. Please log in again.';
            // Clear invalid token
            localStorage.removeItem('token');
          } else if (err.message.includes('timeout')) {
            errorMessage = 'Connection timeout. Please check your internet connection.';
          } else if (err.message.includes('ECONNREFUSED')) {
            errorMessage = 'Cannot connect to server. Please check if the server is running.';
          } else {
            errorMessage = err.message;
          }
        }
        
        setError(errorMessage);
        setConnectionStatus('error');
      });

      // Wire core events into messageHandlers so UI can subscribe
      socket.on('message:new', (data: any) => {
        const handler = messageHandlers.current.get('message');
        if (handler) handler(data as Message);
      });

      socket.on('typing:update', (data: any) => {
        const handler = messageHandlers.current.get('typing');
        if (handler) handler(data as TypingIndicator);
      });

      socket.on('message:read', (data: any) => {
        const handler = messageHandlers.current.get('read_receipt');
        if (handler) handler(data);
      });

      socket.on('conversation:updated', (data: any) => {
        const handler = messageHandlers.current.get('conversation_update');
        if (handler) handler(data);
      });

      // Match request events
      socket.on('matchRequest:new', (data: any) => {
        const handler = messageHandlers.current.get('matchRequest:new');
        if (handler) handler(data);
      });

      socket.on('matchRequest:accepted', (data: any) => {
        const handler = messageHandlers.current.get('matchRequest:accepted');
        if (handler) handler(data);
      });

      socket.on('matchRequest:declined', (data: any) => {
        const handler = messageHandlers.current.get('matchRequest:declined');
        if (handler) handler(data);
      });

      // Condo review events
      socket.on('condoReview:approved', (data: any) => {
        const handler = messageHandlers.current.get('condoReview:approved');
        if (handler) handler(data);
      });

      socket.on('condoReview:declined', (data: any) => {
        const handler = messageHandlers.current.get('condoReview:declined');
        if (handler) handler(data);
      });
    } catch (err) {
      setError((err as Error).message);
      setConnectionStatus('error');
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionStatus('disconnected');
    
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  // Send events via Socket.io
  const sendMessage = useCallback((message: WebSocketMessage) => {
    if (!isConnected) {
      console.warn('WebSocket not connected, message not sent:', message);
      return false;
    }

    if (!socketRef.current) return false;

    switch (message.type) {
      case 'message':
        socketRef.current.emit('message:send', message.data);
        break;
      case 'typing':
        socketRef.current.emit('typing:start', message.data);
        break;
      case 'read_receipt':
        socketRef.current.emit('message:read', message.data);
        break;
      default:
        // Allow custom passthrough
        socketRef.current.emit(message.type, message.data);
        break;
    }

    return true;
  }, [isConnected]);

  // Typing indicator helper (emits typing:start or typing:stop based on isTyping)
  const sendTypingIndicator = useCallback((typingIndicator: TypingIndicator) => {
    if (!isConnected || !socketRef.current) {
      console.warn('WebSocket not connected, typing indicator not sent');
      return false;
    }

    // Emit typing:start or typing:stop based on isTyping flag
    if (typingIndicator.isTyping) {
      socketRef.current.emit('typing:start', {
        conversationId: typingIndicator.conversationId,
        userId: typingIndicator.userId,
        timestamp: typingIndicator.timestamp
      });
    } else {
      socketRef.current.emit('typing:stop', {
        conversationId: typingIndicator.conversationId,
        userId: typingIndicator.userId,
        timestamp: typingIndicator.timestamp
      });
    }

    return true;
  }, [isConnected]);

  // Read receipt helper
  const sendReadReceipt = useCallback((conversationId: string, messageIds: string[]) => {
    return sendMessage({
      type: 'read_receipt',
      data: { conversationId, messageIds, timestamp: new Date().toISOString() }
    });

  }, [sendMessage]);

  // Simulate user going offline
  const simulateUserOffline = useCallback((userId: string) => {
    const handler = messageHandlers.current.get('user_offline');
    if (handler) {
      handler({ userId, timestamp: new Date().toISOString() });
    }
  }, []);

  // Simulate user coming online
  const simulateUserOnline = useCallback((userId: string) => {
    const handler = messageHandlers.current.get('user_online');
    if (handler) {
      handler({ userId, timestamp: new Date().toISOString() });
    }
  }, []);

  // Auto-reconnect logic with exponential backoff
  useEffect(() => {
    if (connectionStatus === 'disconnected' && reconnectAttempts.current < maxReconnectAttempts) {
      const backoffDelay = Math.pow(2, reconnectAttempts.current) * 1000;
      const timeout = setTimeout(() => {
        reconnectAttempts.current++;
        connect();
      }, backoffDelay);
      
      reconnectTimeoutRef.current = timeout;
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connectionStatus, connect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    isConnected,
    connectionStatus,
    error,
    connect,
    disconnect,
    sendMessage,
    sendTypingIndicator,
    sendReadReceipt,
    onMessage,
    offMessage,
    simulateUserOffline,
    simulateUserOnline
  };
};